import React, { useState } from "react";
import { Loader2, AlertTriangle, LogIn, UserPlus, KeyRound, CheckCircle2 } from "lucide-react";
import { ROLE_OPTIONS } from "@/lib/extraQuizData";
import { extraApi, normalizeBirthDate, apiDetail } from "./extraShared";

const inputCls = "w-full px-4 py-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-azure/50 focus:border-brand-azure";
const labelCls = "text-xs font-bold text-slate-500";

function Err({ msg }) {
  if (!msg) return null;
  return (
    <div className="flex items-center gap-2 text-xs font-bold text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">
      <AlertTriangle size={14} className="shrink-0" /> {msg}
    </div>
  );
}

const EMPTY_REG = { firstName: "", lastName: "", email: "", phone: "", birthDate: "", city: "", role: "", password: "", password2: "", privacyConsent: false };

export default function ExtraAuth({ onAuthed }) {
  const [tab, setTab] = useState("register"); // register | login | recovery
  const [reg, setReg] = useState(EMPTY_REG);
  const [log, setLog] = useState({ email: "", password: "" });
  const [rec, setRec] = useState("");
  const [recSent, setRecSent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const setR = (k, v) => setReg((p) => ({ ...p, [k]: v }));

  const switchTab = (t) => { setTab(t); setError(""); setRecSent(""); };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!reg.firstName.trim()) return setError("Il nome è obbligatorio");
    if (!reg.lastName.trim()) return setError("Il cognome è obbligatorio");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(reg.email.trim())) return setError("Inserisci una email valida");
    if (!reg.phone.trim() || reg.phone.trim().length < 5) return setError("Il cellulare è obbligatorio");
    if (!reg.birthDate.trim()) return setError("La data di nascita è obbligatoria");
    if (!normalizeBirthDate(reg.birthDate)) return setError("Inserisci una data valida nel formato GG/MM/AAAA");
    if (!reg.city.trim()) return setError("La città è obbligatoria");
    if (!reg.role) return setError("Seleziona il tuo ruolo");
    if ((reg.password || "").length < 8) return setError("La password deve avere almeno 8 caratteri");
    if (reg.password !== reg.password2) return setError("Le due password non coincidono");
    if (!reg.privacyConsent) return setError("Il consenso privacy è obbligatorio");
    setError(""); setBusy(true);
    try {
      const { data } = await extraApi.post("/extra/auth/register", {
        firstName: reg.firstName.trim(), lastName: reg.lastName.trim(),
        email: reg.email.trim(), phone: reg.phone.trim(),
        birthDate: reg.birthDate.trim(), city: reg.city.trim(), role: reg.role,
        password: reg.password, privacyConsent: true,
      });
      onAuthed(data.token, data.user);
    } catch (e2) {
      setError(apiDetail(e2, "Registrazione non riuscita. Riprova."));
    } finally { setBusy(false); }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    if (!log.email.trim() || !log.password) return setError("Inserisci email e password");
    setError(""); setBusy(true);
    try {
      const { data } = await extraApi.post("/extra/auth/login", { email: log.email.trim(), password: log.password });
      onAuthed(data.token, data.user);
    } catch (e2) {
      setError(apiDetail(e2, "Accesso non riuscito. Riprova."));
    } finally { setBusy(false); }
  };

  const doRecovery = async (e) => {
    e.preventDefault();
    if (!rec.trim()) return setError("Inserisci la tua email o il tuo cellulare");
    setError(""); setBusy(true);
    try {
      const { data } = await extraApi.post("/extra/auth/recovery", { contact: rec.trim() });
      setRecSent(data.message || "Se il contatto è presente, riceverai le istruzioni di recupero.");
    } catch (e2) {
      setError(apiDetail(e2, "Richiesta non riuscita. Riprova."));
    } finally { setBusy(false); }
  };

  const tabs = [
    { id: "register", label: "Registra nuovo account", icon: UserPlus },
    { id: "login", label: "Accedi", icon: LogIn },
    { id: "recovery", label: "Recupera password", icon: KeyRound },
  ];

  return (
    <div className="bg-white rounded-lg p-5 sm:p-8 shadow-xl" data-testid="extra-auth">
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            data-testid={`auth-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold border transition-colors ${tab === t.id ? "bg-brand-navy text-white border-brand-navy" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "register" && (
        <form onSubmit={doRegister} className="mt-5 grid gap-4" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="reg-first-name" className={labelCls}>Nome *</label>
              <input id="reg-first-name" data-testid="reg-first-name" className={inputCls} value={reg.firstName} onChange={(e) => setR("firstName", e.target.value)} placeholder="Nome" autoComplete="given-name" />
            </div>
            <div>
              <label htmlFor="reg-last-name" className={labelCls}>Cognome *</label>
              <input id="reg-last-name" data-testid="reg-last-name" className={inputCls} value={reg.lastName} onChange={(e) => setR("lastName", e.target.value)} placeholder="Cognome" autoComplete="family-name" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="reg-email" className={labelCls}>Email *</label>
              <input id="reg-email" data-testid="reg-email" type="email" className={inputCls} value={reg.email} onChange={(e) => setR("email", e.target.value)} placeholder="nome@studio.it" autoComplete="email" />
            </div>
            <div>
              <label htmlFor="reg-phone" className={labelCls}>Cellulare *</label>
              <input id="reg-phone" data-testid="reg-phone" type="tel" className={inputCls} value={reg.phone} onChange={(e) => setR("phone", e.target.value)} placeholder="+39 ..." autoComplete="tel" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="reg-birth-date" className={labelCls}>Data di nascita *</label>
              <input id="reg-birth-date" data-testid="reg-birth-date" type="text" inputMode="numeric" placeholder="GG/MM/AAAA" className={inputCls} value={reg.birthDate} onChange={(e) => setR("birthDate", e.target.value)} autoComplete="bday" />
            </div>
            <div>
              <label htmlFor="reg-city" className={labelCls}>Città *</label>
              <input id="reg-city" data-testid="reg-city" className={inputCls} value={reg.city} onChange={(e) => setR("city", e.target.value)} placeholder="Città" autoComplete="address-level2" />
            </div>
          </div>
          <div>
            <label htmlFor="reg-role" className={labelCls}>Ruolo *</label>
            <select id="reg-role" data-testid="reg-role" className={inputCls} value={reg.role} onChange={(e) => setR("role", e.target.value)}>
              <option value="">Seleziona il tuo ruolo…</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="reg-password" className={labelCls}>Password (min 8 caratteri) *</label>
              <input id="reg-password" data-testid="reg-password" type="password" className={inputCls} value={reg.password} onChange={(e) => setR("password", e.target.value)} placeholder="••••••••" autoComplete="new-password" />
            </div>
            <div>
              <label htmlFor="reg-password2" className={labelCls}>Conferma password *</label>
              <input id="reg-password2" data-testid="reg-password2" type="password" className={inputCls} value={reg.password2} onChange={(e) => setR("password2", e.target.value)} placeholder="••••••••" autoComplete="new-password" />
            </div>
          </div>
          <label className="flex items-start gap-3 text-xs text-slate-600 leading-relaxed cursor-pointer">
            <input data-testid="reg-privacy" type="checkbox" className="mt-0.5 w-4 h-4 accent-[#2A9FCB]" checked={reg.privacyConsent} onChange={(e) => setR("privacyConsent", e.target.checked)} />
            <span>Acconsento al trattamento dei dati inseriti per accedere ai contenuti extra e ricevere comunicazioni collegate a Prime Vision. *</span>
          </label>
          <Err msg={error} />
          <button type="submit" disabled={busy} data-testid="reg-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-6 py-3.5 rounded-lg text-sm transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={15} />} Crea account e continua
          </button>
          <p className="text-[11px] text-slate-400 text-center">Nessun dato sanitario viene richiesto o salvato. La password è protetta con hash sicuro.</p>
        </form>
      )}

      {tab === "login" && (
        <form onSubmit={doLogin} className="mt-5 grid gap-4" data-testid="login-form">
          <div>
            <label htmlFor="login-email" className={labelCls}>Email *</label>
            <input id="login-email" data-testid="login-email" type="email" className={inputCls} value={log.email} onChange={(e) => setLog((p) => ({ ...p, email: e.target.value }))} placeholder="nome@studio.it" autoComplete="email" />
          </div>
          <div>
            <label htmlFor="login-password" className={labelCls}>Password *</label>
            <input id="login-password" data-testid="login-password" type="password" className={inputCls} value={log.password} onChange={(e) => setLog((p) => ({ ...p, password: e.target.value }))} placeholder="••••••••" autoComplete="current-password" />
          </div>
          <Err msg={error} />
          <button type="submit" disabled={busy} data-testid="login-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-6 py-3.5 rounded-lg text-sm transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={15} />} Accedi e continua
          </button>
        </form>
      )}

      {tab === "recovery" && (
        <form onSubmit={doRecovery} className="mt-5 grid gap-4" data-testid="recovery-form">
          <p className="text-xs text-slate-500 leading-relaxed">Inserisci l'email o il cellulare usati per registrarti: se il contatto è presente riceverai le istruzioni per reimpostare la password.</p>
          <div>
            <label htmlFor="recovery-contact" className={labelCls}>Email o cellulare *</label>
            <input id="recovery-contact" data-testid="recovery-contact" className={inputCls} value={rec} onChange={(e) => setRec(e.target.value)} placeholder="nome@studio.it oppure +39 ..." />
          </div>
          {recSent ? (
            <div className="flex items-start gap-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="recovery-sent">
              <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> {recSent}
            </div>
          ) : <Err msg={error} />}
          <button type="submit" disabled={busy} data-testid="recovery-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-6 py-3.5 rounded-lg text-sm transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={15} />} Invia istruzioni di recupero
          </button>
        </form>
      )}
    </div>
  );
}
